import { useMemo } from 'react'
import GcpRuleBasedCallout from '@/components/gcp/GcpRuleBasedCallout'
import GcpSectionErrorBanner from '@/components/gcp/GcpSectionErrorBanner'
import GcpStatCard from '@/components/gcp/GcpStatCard'
import {
  asNumber,
  asRecordArray,
  asString,
  formatUsd,
  getSectionData,
  type RuleBasedRecommendation,
} from '@/components/gcp/gcpHelpers'
import type { GcpAnalysisResult, GcpMiscSectionData } from '@/services/gcpCosts'
import { isGcpSectionError } from '@/services/gcpCosts'

interface GcpMiscSectionProps {
  analysis: GcpAnalysisResult
}

function formatMomChange(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  const prefix = value > 0 ? '+' : ''
  return `${prefix}${formatUsd(value)}`
}

export default function GcpMiscSection({ analysis }: GcpMiscSectionProps) {
  const section = analysis.sections.misc
  const data = getSectionData(analysis, 'misc') as GcpMiscSectionData | null

  const recommendations = useMemo(
    () =>
      data
        ? (asRecordArray(data.recommendations) as RuleBasedRecommendation[])
        : [],
    [data]
  )

  const services = useMemo(
    () =>
      (data ? asRecordArray(data.services) : []).map((row) => ({
        service: asString(row.service),
        run_rate_monthly_usd: asNumber(row.run_rate_monthly_usd) ?? 0,
        mom_change_usd: asNumber(row.mom_change_usd),
        skus: asRecordArray(row.skus).map((sku) => ({
          sku: asString(sku.sku),
          net_cost: asNumber(sku.net_cost) ?? 0,
          usage_amount: asNumber(sku.usage_amount),
          usage_unit: sku.usage_unit == null ? null : asString(sku.usage_unit),
        })),
      })),
    [data]
  )

  const taxItems = useMemo(
    () =>
      (data ? asRecordArray(data.tax_items) : []).map((row) => ({
        service: asString(row.service),
        label: asString(row.label) || 'Sales tax',
        run_rate_monthly_usd: asNumber(row.run_rate_monthly_usd) ?? 0,
      })),
    [data]
  )

  if (isGcpSectionError(section)) {
    return (
      <GcpSectionErrorBanner
        title="Everything else analysis failed"
        error={section.error}
        missingRoleHint={section.missing_role_hint}
      />
    )
  }

  if (!data) return null

  const headline = asNumber(data.headline_run_rate_usd_monthly) ?? 0

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-500">
        Long-tail GCP services not covered by dedicated analyzers — Secret Manager,
        Artifact Registry, and other billing lines with targeted savings rules.
      </p>

      <GcpStatCard
        label="Misc services run rate"
        value={`${formatUsd(headline)}/mo`}
        subtext="Optimizable long-tail spend (excludes sales tax)"
      />

      {taxItems.length > 0 ? (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
          <p className="font-medium text-gray-800">Sales tax</p>
          <p className="mt-1 text-gray-600">
            Tax billing is shown for completeness and excluded from optimization totals.
          </p>
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Label</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Service</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Run rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {taxItems.map((item) => (
                  <tr key={item.service}>
                    <td className="px-3 py-2 text-gray-800">{item.label}</td>
                    <td className="px-3 py-2 text-gray-600">{item.service}</td>
                    <td className="px-3 py-2 text-right text-gray-800">
                      {formatUsd(item.run_rate_monthly_usd)}/mo
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {services.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Service</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">Run rate</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">MoM Δ</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Top SKUs</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {services.map((svc) => (
                <tr key={svc.service}>
                  <td className="px-3 py-2 font-medium text-gray-800">{svc.service}</td>
                  <td className="px-3 py-2 text-right text-gray-800">
                    {formatUsd(svc.run_rate_monthly_usd)}/mo
                  </td>
                  <td className="px-3 py-2 text-right text-gray-600">
                    {formatMomChange(svc.mom_change_usd)}
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {svc.skus.length === 0 ? (
                      '—'
                    ) : (
                      <ul className="space-y-1">
                        {svc.skus.slice(0, 3).map((sku) => (
                          <li key={sku.sku}>
                            {sku.sku}: {formatUsd(sku.net_cost)}
                            {sku.usage_amount != null && sku.usage_unit
                              ? ` (${sku.usage_amount.toLocaleString()} ${sku.usage_unit})`
                              : ''}
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-gray-500">No misc services above the noise floor.</p>
      )}

      {recommendations.length > 0 ? (
        <div className="space-y-3">
          <h3 className="container-title">Rule-based findings</h3>
          {recommendations.map((rec, index) => (
            <GcpRuleBasedCallout key={`${rec.type ?? 'rec'}-${index}`} recommendation={rec} />
          ))}
        </div>
      ) : null}
    </div>
  )
}
